import React, { useState, useEffect } from "react";
import { useFormatter } from "../../../hooks/useFomatter";
import { useServerApi } from "../../../hooks/userServerApi";
import {
  IconRefresh,
  IconCreditCard,
  IconFileText,
} from "@tabler/icons";
import {
  Button,
  Text,
  Title,
  Badge,
  Tabs,
  LoadingOverlay,
  Box,
  Paper,
  Table,
  Grid,
  Group,
  Stack,
  SimpleGrid,
} from "@mantine/core";
import moment from "moment";
import EntityTabAnchor from "./EntityTabAnchor";
import DuplicateBidConflictsAlert from "./DuplicateBidConflictsAlert";

const showPolicyStatus =
  process.env.REACT_APP_PAYROLL_SHOW_POLICY_STATUS === "true";

const getPolicyStatusColor = (status) => {
  if (status === "POLICY_RECEIVED") return "green";
  if (status === "POLICY_DISPATCHED") return "blue";
  return "gray";
};

/**
 * Sums totalRcv per order across all CONFIRMED statement rows in the payroll
 * period search, and records each contributing line (for transparency when the
 * same order appears on multiple statements).
 */
const aggregateCommissionFromStatements = (statements) => {
  const orderCommissionMap = {};
  const orderCommissionBreakdown = {};
  if (!statements?.length) {
    return { orderCommissionMap, orderCommissionBreakdown };
  }
  statements.forEach((statement) => {
    const statementId = statement._id;
    const statementCode = statement.code || "-";
    const statementPeriod = statement.period || "";
    if (!statement.rows) return;
    statement.rows.forEach((row) => {
      if (row.status === "CONFIRMED" && row.order) {
        const orderId =
          typeof row.order === "object"
            ? row.order._id?.toString()
            : row.order?.toString();
        if (!orderId) return;
        const rcv = row.totalRcv || 0;
        orderCommissionMap[orderId] =
          (orderCommissionMap[orderId] || 0) + rcv;
        if (!orderCommissionBreakdown[orderId]) {
          orderCommissionBreakdown[orderId] = [];
        }
        orderCommissionBreakdown[orderId].push({
          statementId,
          statementCode,
          statementPeriod,
          totalRcv: rcv,
          rowNumber: row.rowNumber,
        });
      }
    });
  });
  return { orderCommissionMap, orderCommissionBreakdown };
};

const OrdersTab = ({ form }) => {
  const [api] = useServerApi();
  const [formatter] = useFormatter();

  const [loading, setLoading] = useState(false);
  const [debitNotes, setDebitNotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [excludedConflicts, setExcludedConflicts] = useState([]);

  const payrollStatus = form?.values?.status;
  const isConfirmed =
    payrollStatus === "CONFIRMED" || payrollStatus === "COMPLETED";

  const loadSkippedDuplicateConflicts = async () => {
    if (!form?.values?._id) {
      setExcludedConflicts([]);
      return;
    }
    try {
      const result = await api.Payroll2.getDuplicateBidConflicts(
        form.values._id,
      );
      if (result?.success) {
        setExcludedConflicts(
          result.data?.excludedConflicts ||
            result.data?.skippedDuplicateSources ||
            result.data?.conflicts ||
            [],
        );
      }
    } catch (error) {
      console.log("loadSkippedDuplicateConflicts error:", error);
    }
  };

  useEffect(() => {
    const loadSavedData = async () => {
      if (!form?.values?._id) {
        setDebitNotes([]);
        setOrders([]);
        return;
      }
      try {
        setLoading(true);
        const result = await api.Payroll2.getOrdersTabTable(form.values._id);
        if (result?.success && result.data) {
          setDebitNotes(result.data.debitNotes || []);
          setOrders(result.data.orders || []);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedData();
    loadSkippedDuplicateConflicts();
  }, [form?.values?._id]);

  const handleUpdate = async () => {
    try {
      setLoading(true);

      let fetchedDebitNotes = [];
      let fetchedOrders = [];

      const savedClientPaymentSearch = form?.values?.clientPaymentSearch;
      if (savedClientPaymentSearch) {
        const clientPaymentsResult =
          await api.Payroll2.getClientPaymentsForPayroll(
            moment(savedClientPaymentSearch.fromDate).format("YYYY-MM-DD"),
            moment(savedClientPaymentSearch.toDate).format("YYYY-MM-DD"),
            null,
          );

        if (
          clientPaymentsResult?.success &&
          clientPaymentsResult.data?.payments
        ) {
          console.log(
            `[DEBUG] Fetched ${clientPaymentsResult.data.payments.length} client payments`,
          );
          const debitNoteIds = [];
          const dnPaymentMap = {};

          clientPaymentsResult.data.payments.forEach((cp, index) => {
            if (index === 0) {
              console.log("[DEBUG] First payment sample:", {
                code: cp.code,
                hasDebitNoteAssignments: !!cp.debitNoteAssignments,
                assignmentsLength: cp.debitNoteAssignments?.length || 0,
              });
            }
            if (cp.debitNoteAssignments) {
              cp.debitNoteAssignments.forEach((assignment) => {
                if (assignment.debitNote) {
                  const dnId =
                    typeof assignment.debitNote === "object"
                      ? assignment.debitNote._id?.toString()
                      : assignment.debitNote?.toString();
                  if (dnId) {
                    if (!debitNoteIds.includes(dnId)) {
                      debitNoteIds.push(dnId);
                      dnPaymentMap[dnId] = assignment.paymentAmount || 0;
                    } else {
                      dnPaymentMap[dnId] =
                        (dnPaymentMap[dnId] || 0) +
                        (assignment.paymentAmount || 0);
                    }
                  }
                }
              });
            }
          });

          console.log(
            `[DEBUG] Extracted ${debitNoteIds.length} unique debit note IDs`,
          );

          if (debitNoteIds.length > 0) {
            console.log("[DEBUG] Calling getDebitNotesForPayroll...");
            const debitNotesResult = await api.Payroll2.getDebitNotesForPayroll(
              debitNoteIds,
              { paymentStatus: { $in: ["PAID", "SETTLED"] } },
            );

            if (
              debitNotesResult?.success &&
              debitNotesResult.data?.debitNotes
            ) {
              fetchedDebitNotes = debitNotesResult.data.debitNotes.map(
                (dn) => ({
                  ...dn,
                  assignmentPayment: dnPaymentMap[dn._id?.toString()] || 0,
                }),
              );
              console.log(
                `[DEBUG] Fetched ${fetchedDebitNotes.length} debit notes`,
              );
            }
          } else {
            console.log("[DEBUG] No debit note IDs found in client payments");
          }
        }
      }

      const savedCommissionStatementSearch =
        form?.values?.commissionStatementSearch;
      if (savedCommissionStatementSearch) {
        const statementsResult =
          await api.Payroll2.getCommissionStatementsForPayroll(
            moment(savedCommissionStatementSearch.fromPeriod).format(
              "YYYY-MM-DD",
            ),
            moment(savedCommissionStatementSearch.toPeriod).format(
              "YYYY-MM-DD",
            ),
            null,
          );

        if (statementsResult?.success && statementsResult.data?.statements) {
          console.log(
            `[DEBUG] Fetched ${statementsResult.data.statements.length} commission statements`,
          );
          const { orderCommissionMap, orderCommissionBreakdown } =
            aggregateCommissionFromStatements(statementsResult.data.statements);
          const orderIds = Object.keys(orderCommissionMap);

          console.log(`[DEBUG] Extracted ${orderIds.length} unique order IDs`);

          if (orderIds.length > 0) {
            console.log("[DEBUG] Calling getOrdersForPayroll...");
            const ordersResult =
              await api.Payroll2.getOrdersForPayroll(orderIds);

            if (ordersResult?.success && ordersResult.data?.orders) {
              fetchedOrders = ordersResult.data.orders.map((order) => ({
                ...order,
                commissionReceived:
                  orderCommissionMap[order._id?.toString()] || 0,
                commissionFromStatementsBreakdown:
                  orderCommissionBreakdown[order._id?.toString()] || [],
              }));
              console.log(`[DEBUG] Fetched ${fetchedOrders.length} orders`);
            }
          } else {
            console.log("[DEBUG] No order IDs found in commission statements");
          }
        }
      }

      setDebitNotes(fetchedDebitNotes);
      setOrders(fetchedOrders);

      if (form?.values?._id) {
        const filterResult = await api.Payroll2.filterDispatchedSourceConflicts(
          form.values._id,
          {
            debitNoteIds: fetchedDebitNotes.map((dn) => dn._id),
            orderIds: fetchedOrders.map((o) => o._id),
          },
        );

        const allowedDebitNoteIds = new Set(
          (filterResult?.data?.debitNoteIds || []).map((id) => id.toString()),
        );
        const allowedOrderIds = new Set(
          (filterResult?.data?.orderIds || []).map((id) => id.toString()),
        );
        const filteredDebitNotes = fetchedDebitNotes.filter((dn) =>
          allowedDebitNoteIds.has(dn._id?.toString()),
        );
        const filteredOrders = fetchedOrders.filter((o) =>
          allowedOrderIds.has(o._id?.toString()),
        );
        const excluded = filterResult?.data?.excludedConflicts || [];

        setDebitNotes(filteredDebitNotes);
        setOrders(filteredOrders);
        setExcludedConflicts(excluded);

        await api.updateById({
          apiEntity: "payroll",
          id: form.values._id,
          values: {
            debitNotesFromClientPayments: filteredDebitNotes.map((dn) => dn._id),
            ordersFromCommissionStatements: filteredOrders.map((o) => o._id),
          },
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "PAID":
        return "green";
      case "SETTLED":
        return "blue";
      case "DIRECT":
        return "blue";
      case "PARTIAL":
        return "orange";
      case "DFT":
        return "yellow";
      case "WAIT":
        return "red";
      case "MATCHING":
        return "orange";
      case "CANCELLED":
        return "red";
      case "EN_NO_PAYMENT":
        return "green";
      default:
        return "gray";
    }
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} overlayBlur={2} />

      <Paper p="md" withBorder mb="lg">
        <Group position="apart">
          <Box>
            <Title order={5}>Orders / Debit Notes</Title>
            <Text size="xs" color="dimmed" mt="xs">
              Click Update to load Debit Notes from Client Payments and Orders
              from Commission Statements
            </Text>
          </Box>
          <Button
            size="xs"
            leftIcon={<IconRefresh size={14} />}
            onClick={handleUpdate}
            disabled={isConfirmed}
          >
            Update
          </Button>
        </Group>
        {isConfirmed && (
          <Text size="xs" color="orange" mt="xs">
            Payroll is completed. No more edits allowed.
          </Text>
        )}
      </Paper>

      <Box mb="md">
        <DuplicateBidConflictsAlert conflicts={excludedConflicts} />
      </Box>

      <Tabs defaultValue="debitNotes" variant="outline">
        <Tabs.List mb="md">
          <Tabs.Tab value="debitNotes" icon={<IconCreditCard size={14} />}>
            From Client Payment ({debitNotes.length})
          </Tabs.Tab>
          <Tabs.Tab value="orders" icon={<IconFileText size={14} />}>
            From Comm. Statement ({orders.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="debitNotes" pt="xs">
          {debitNotes.length > 0 && (
            <Paper p="md" withBorder mb="lg">
              <SimpleGrid cols={3}>
                <Box>
                  <Text size="xs" color="dimmed" transform="uppercase">
                    Debit Notes
                  </Text>
                  <Text size="xl" weight={700}>
                    {debitNotes.length}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" color="dimmed" transform="uppercase">
                    Total Paid Amount
                  </Text>
                  <Text size="xl" weight={700} color="blue">
                    {formatter.currency(
                      debitNotes.reduce(
                        (sum, dn) => sum + (dn.assignmentPayment || 0),
                        0,
                      ),
                    )}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" color="dimmed" transform="uppercase">
                    Total Commission
                  </Text>
                  <Text size="xl" weight={700} color="green">
                    {formatter.currency(
                      debitNotes.reduce(
                        (sum, dn) =>
                          sum +
                          ((dn.total || 0) -
                            (dn.costTotal || 0) -
                            (dn.discountAmount || 0)),
                        0,
                      ),
                    )}
                  </Text>
                </Box>
              </SimpleGrid>
            </Paper>
          )}

          {debitNotes.length > 0 ? (
            <Paper p="md" withBorder>
              <Group position="apart" mb="md">
                <Title order={5}>Debit Notes from Client Payments</Title>
                <Badge size="lg">{debitNotes.length} debit notes found</Badge>
              </Group>

              <Table fontSize="sm" striped highlightOnHover>
                <thead>
                  <tr>
                    <th>DN Code</th>
                    <th>Type</th>
                    <th>Client</th>
                    <th>Product</th>
                    {showPolicyStatus && <th>Policy</th>}
                    <th>Paid Amount</th>
                    <th>Commission</th>
                    <th>Payment Status</th>
                  </tr>
                </thead>
                <tbody>
                  {debitNotes.map((dn) => (
                    <tr key={dn._id}>
                      <td>
                        <EntityTabAnchor
                          entity="debitNote"
                          id={dn._id}
                          label={dn.code}
                          size="sm"
                        />
                      </td>
                      <td>
                        <Text size="sm">{dn.type || "-"}</Text>
                      </td>
                      <td>
                        <Box>
                          <Text size="sm" weight={500} lineClamp={1}>
                            {dn.client?.name || "-"}
                          </Text>
                          <Text size="xs" color="dimmed">
                            {dn.client?.code || "-"}
                          </Text>
                        </Box>
                      </td>
                      <td>
                        <Box>
                          <Text size="sm" weight={500} lineClamp={1}>
                            {dn.provider?.name || "-"}
                          </Text>
                          <Text size="xs" color="dimmed" lineClamp={1}>
                            {dn.productType?.name ||
                              dn.product?.type?.name ||
                              "-"}
                          </Text>
                        </Box>
                      </td>
                      {showPolicyStatus && (
                        <td>
                          <Badge
                            size="xs"
                            radius={0}
                            color={getPolicyStatusColor(dn.policyStatus)}
                            mb={2}
                          >
                            {dn.policyStatus || "NONE"}
                          </Badge>
                          <Text size="xs" lineClamp={1}>
                            {dn.policyNumber || "-"}
                          </Text>
                        </td>
                      )}
                      <td>
                        <Text size="sm">
                          {formatter.currency(dn.payment?.amount)}
                        </Text>
                      </td>
                      <td>
                        <Text
                          size="sm"
                          color={
                            (dn.total || 0) -
                              (dn.costTotal || 0) -
                              (dn.discountAmount || 0) >=
                            0
                              ? "green"
                              : "red"
                          }
                        >
                          {formatter.currency(
                            (dn.total || 0) -
                              (dn.costTotal || 0) -
                              (dn.discountAmount || 0),
                          )}
                        </Text>
                      </td>
                      <td>
                        <Badge
                          color={getPaymentStatusColor(dn.paymentStatus)}
                          size="sm"
                        >
                          {dn.paymentStatus}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Paper>
          ) : (
            <Paper p="xl" withBorder>
              <Text color="dimmed" align="center">
                No debit notes found. Please save Client Payments first, then
                click Update.
              </Text>
            </Paper>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="orders" pt="xs">
          {orders.length > 0 && (
            <Paper p="md" withBorder mb="lg">
              <SimpleGrid cols={3}>
                <Box>
                  <Text size="xs" color="dimmed" transform="uppercase">
                    Orders
                  </Text>
                  <Text size="xl" weight={700}>
                    {orders.length}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" color="dimmed" transform="uppercase">
                    Total Commission Received
                  </Text>
                  <Text size="xl" weight={700} color="green">
                    {formatter.currency(
                      orders.reduce(
                        (sum, order) => sum + (order.commissionReceived || 0),
                        0,
                      ),
                    )}
                  </Text>
                </Box>
                <Box />
              </SimpleGrid>
            </Paper>
          )}

          {orders.length > 0 ? (
            <Paper p="md" withBorder>
              <Group position="apart" mb="md">
                <Title order={5}>Orders from Commission Statements</Title>
                <Badge size="lg">{orders.length} orders found</Badge>
              </Group>
              <Text size="xs" color="dimmed" mb="md">
                The commission total sums Total Rcv from every confirmed
                statement row in this payroll&apos;s period search for that
                order. When the same order appears on more than one statement,
                each line is listed below the total.
              </Text>

              <Table fontSize="sm" striped highlightOnHover>
                <thead>
                  <tr>
                    <th>Order Code</th>
                    <th>DN Code</th>
                    <th>Client</th>
                    <th>Product</th>
                    {showPolicyStatus ? <th>Policy</th> : <th>Policy Number</th>}
                    <th>Commission</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order._id}>
                      <td>
                        <EntityTabAnchor
                          entity="order"
                          id={order._id}
                          label={order.code}
                          size="sm"
                        />
                      </td>
                      <td>
                        <EntityTabAnchor
                          entity="debitNote"
                          id={order.debitNote?._id || order.debitNote}
                          label={order.debitNote?.code}
                          size="sm"
                        />
                      </td>
                      <td>
                        <Box>
                          <Text size="sm" weight={500} lineClamp={1}>
                            {order.client?.name || "-"}
                          </Text>
                          <Text size="xs" color="dimmed">
                            {order.client?.code || "-"}
                          </Text>
                        </Box>
                      </td>
                      <td>
                        <Box>
                          <Text size="sm" weight={500} lineClamp={1}>
                            {order.provider?.name || "-"}
                          </Text>
                          <Text size="xs" color="dimmed" lineClamp={1}>
                            {order.debitNote?.productType?.name ||
                              order.product?.type?.name ||
                              order.product?.code ||
                              "-"}
                          </Text>
                        </Box>
                      </td>
                      {showPolicyStatus ? (
                        <td>
                          <Badge
                            size="xs"
                            radius={0}
                            color={getPolicyStatusColor(
                              order.policyStatus ||
                                order.debitNote?.policyStatus,
                            )}
                            mb={2}
                          >
                            {order.policyStatus ||
                              order.debitNote?.policyStatus ||
                              "NONE"}
                          </Badge>
                          <Text size="xs" lineClamp={1}>
                            {order.policyNumber ||
                              order.debitNote?.policyNumber ||
                              "-"}
                          </Text>
                        </td>
                      ) : (
                        <td>
                          <Text size="sm">{order.policyNumber || "-"}</Text>
                        </td>
                      )}
                      <td>
                        <Stack spacing={4}>
                          <Text
                            size="sm"
                            weight={500}
                            color={
                              (order.commissionReceived || 0) >= 0
                                ? "green"
                                : "red"
                            }
                          >
                            {formatter.currency(order.commissionReceived || 0)}
                          </Text>
                          {order.commissionFromStatementsBreakdown?.length >
                            0 && (
                            <Stack spacing={2}>
                              {order.commissionFromStatementsBreakdown.map(
                                (line, idx) => (
                                  <Text
                                    key={`${line.statementId}-${idx}`}
                                    size="xs"
                                    color="dimmed"
                                  >
                                    <EntityTabAnchor
                                      entity="commissionStatement"
                                      id={line.statementId}
                                      label={line.statementCode}
                                      size="xs"
                                    />
                                    {line.statementPeriod
                                      ? ` (${line.statementPeriod})`
                                      : ""}
                                    : {formatter.currency(line.totalRcv)}
                                    {line.rowNumber != null &&
                                    line.rowNumber !== ""
                                      ? ` · row #${line.rowNumber}`
                                      : ""}
                                  </Text>
                                ),
                              )}
                            </Stack>
                          )}
                        </Stack>
                      </td>
                      <td>
                        <Group spacing="1" noWrap>
                          <Badge
                            color={order.status === "ACTIVE" ? "green" : "gray"}
                            size="sm"
                          >
                            {order.status || "-"}
                          </Badge>
                          {order.debitNote?.paymentStatus && (
                            <Badge
                              color={getPaymentStatusColor(
                                order.debitNote.paymentStatus,
                              )}
                              size="sm"
                            >
                              {order.debitNote.paymentStatus}
                            </Badge>
                          )}
                        </Group>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Paper>
          ) : (
            <Paper p="xl" withBorder>
              <Text color="dimmed" align="center">
                No orders found. Please save Commission Statements first, then
                click Update.
              </Text>
            </Paper>
          )}
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
};

export default OrdersTab;
